import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useDebouncedCallbackPerKey from 'hooks/useDebounceCallbackPerKey';
import FilePlusSvg from 'images/v5/icons/file-plus-01.svg';
import Button from 'layout/atoms/Button/Button';
import ComponentBadge from 'layout/atoms/ComponentBadge/ComponentBadge';
import Header from 'layout/atoms/Header/Header';
import InputNumber from 'layout/atoms/InputNumber';
import Loader from 'layout/atoms/Loader/Loader';
import Switch from 'layout/atoms/Switch/Switch';
import TableGrid, { Body, Cell, Head, Row } from 'layout/atoms/TableGrid';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import Grid from 'layout/molecules/Grid/Grid';
import parseComponentsByCategory from 'parsers/specification/parseComponentsByCategory';
import { openModal } from 'store/layoutSlice';
import { fetchSpecificationComponentConsumptions, selectSpecificationComponentConsumptions, selectSpecificationComponentConsumptionsRefetch, selectSpecificationComponentConsumptionsSpecificationId, selectSpecificationComponentConsumptionsStatus, shouldFetchSpecificationComponentQuery, toggleVariedConsumptionSpecificationComponent, updateConsumptionSpecificationComponent, updateStoreConsumption, } from 'store/specificationComponentsSlice';
import { fetchSpecificationProblems } from 'store/specificationDetailsSlice';
import { fetchSizings, fetchSpecificationProductionQuantities, selectProductionQuantitiesItem, selectProductionQuantitiesStatus, } from 'store/specificationProductionQuantitiesSlice';
import { getConsumptionTypeLabel } from 'utils/convertMetrics';
import getCategoryLabel from 'utils/getCategoryLabel';
function ComponentConsumptions() {
    const { t, i18n } = useTranslation(['specification', 'common']);
    const dispatch = useDispatch();
    const specificationDetails = useSelector((state) => state.specificationDetails.item);
    const isBrandNew = useSelector((state) => state.specificationDetails.brandNewSpec);
    const componentsStatus = useSelector(selectSpecificationComponentConsumptionsStatus);
    const componentsRefetch = useSelector(selectSpecificationComponentConsumptionsRefetch);
    const cachedSpecificationId = useSelector(selectSpecificationComponentConsumptionsSpecificationId);
    const components = useSelector(selectSpecificationComponentConsumptions);
    const categoriezdComponents = parseComponentsByCategory(components);
    const productionQuantitiesItem = useSelector(selectProductionQuantitiesItem);
    const productionQuantitiesStatus = useSelector(selectProductionQuantitiesStatus);
    const sizes = productionQuantitiesItem === null || productionQuantitiesItem === void 0 ? void 0 : productionQuantitiesItem.sizes;
    const hasProblems = (id) => {
        var _a;
        return !isBrandNew &&
            ((_a = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.confirmationProblems) === null || _a === void 0 ? void 0 : _a.some((problem) => {
                var _a;
                return problem.problemType === 'no_component_consumption' &&
                    ((_a = problem.components) === null || _a === void 0 ? void 0 : _a.some((component) => component.nodeId === id));
            }));
    };
    useEffect(() => {
        dispatch(fetchSizings());
        if (shouldFetchSpecificationComponentQuery(componentsStatus, componentsRefetch, cachedSpecificationId, specificationDetails.id)) {
            dispatch(fetchSpecificationComponentConsumptions({ specificationId: specificationDetails.id }));
        }
        if (productionQuantitiesStatus.fetch === 'idle') {
            dispatch(fetchSpecificationProductionQuantities({ specificationId: specificationDetails.id }));
        }
    }, [
        dispatch,
        specificationDetails.id,
        componentsStatus,
        componentsRefetch,
        cachedSpecificationId,
        productionQuantitiesStatus,
    ]);
    const [debouncedUpdate, savingKeys] = useDebouncedCallbackPerKey((cellId, value) => {
        dispatch(updateConsumptionSpecificationComponent({
            cellId,
            value,
        }))
            .unwrap()
            .then(() => {
            dispatch(fetchSpecificationProblems({ specificationId: specificationDetails.id }));
        });
    }, 1000);
    const handleInputChange = (e, componentId) => {
        const { name, value } = e.target;
        dispatch(updateStoreConsumption({
            componentId,
            cellId: name,
            value,
        }));
        debouncedUpdate(name, value);
    };
    if (cachedSpecificationId !== specificationDetails.id ||
        componentsStatus === 'loading' ||
        componentsStatus === 'idle' ||
        productionQuantitiesStatus.fetch === 'loading' ||
        productionQuantitiesStatus.fetch === 'idle') {
        return React.createElement(Loader, null);
    }
    if (componentsStatus === 'succeeded' && components.length === 0) {
        return (React.createElement(EmptyState, { header: t('add_first_component'), subheader: t('add_first_component_subheader') },
            React.createElement(Button, { color: "primary", leftIcon: React.createElement(FilePlusSvg, null), onClick: () => dispatch(openModal({ name: 'componentsSelector' })) }, t('add_components'))));
    }
    return (React.createElement(Grid, { gap: 24, overflow: true },
        React.createElement(Header, { size: "m" }, t('components_consumption')),
        categoriezdComponents.map((category) => (React.createElement(TableGrid, { key: category.categoryId, columnBorders: true, columns: [
                ['400px', '1fr'],
                ['100px', 'auto'],
                ['100px', 'auto'],
                ...sizes
                    .filter((size) => size.isActive)
                    .map(() => ['100px', '1fr']),
            ] },
            React.createElement(Head, null,
                React.createElement(Row, { head: true },
                    React.createElement(Cell, null, t('common:version')),
                    React.createElement(Cell, { span: 2 }, t('consumptionVariedOnSize')),
                    sizes.map((size) => size.isActive && React.createElement(Cell, { key: size.sizeName.id }, size.sizeName.name)))),
            React.createElement(Body, null, category.items.map((component) => (React.createElement(Row, { key: component.id },
                React.createElement(Cell, null,
                    React.createElement(ComponentBadge, { name: component.name, category: getCategoryLabel(component.productCategory, i18n), missingData: hasProblems(component.id) })),
                React.createElement(Cell, null,
                    React.createElement(Switch, { value: !component.consumptionVariedOnSize, onChange: () => dispatch(toggleVariedConsumptionSpecificationComponent({
                            componentId: component.id,
                            consumptionVariedOnSize: !component.consumptionVariedOnSize,
                        })), disabled: specificationDetails.state === 'PRODUCTION' })),
                React.createElement(Cell, null, !component.consumptionVariedOnSize && (React.createElement(InputNumber, { name: component.consumptions.edges[0].node.id, value: component.consumptions.edges[0].node.consumption || 0, suffix: getConsumptionTypeLabel(component.productCategory.consumptionType, i18n), onChange: (e) => handleInputChange(e, component.id), loading: savingKeys.has(component.consumptions.edges[0].node.id), disabled: specificationDetails.state === 'PRODUCTION', decimal: 3 }))),
                !component.consumptionVariedOnSize ? (React.createElement(Cell, { span: sizes.filter((size) => size.isActive).length })) : (component.consumptions.edges.map((value) => (React.createElement(Cell, { key: value.node.id },
                    React.createElement(InputNumber, { name: value.node.id, value: value.node.consumption || 0, suffix: getConsumptionTypeLabel(component.productCategory.consumptionType, i18n), onChange: (e) => handleInputChange(e, component.id), loading: savingKeys.has(value.node.id), disabled: specificationDetails.state === 'PRODUCTION', decimal: 3 }))))))))))))));
}
export default ComponentConsumptions;
